import type {
  Alarm,
  AlarmRecord,
  AssetConfig,
  AssetFlexibilityState,
  AssetHistory,
  DispatchEvent,
  EventTimelineEntry,
  FleetSite,
  ForecastView,
  GridCapacityObject,
  MarketView,
  PortfolioDashboard,
  PortfolioState,
  Principal,
  SiteApiStatus,
  SiteDashboard,
  SiteEventRecord,
  SiteOperations,
  SiteStatus,
  SiteTimeline,
  ThermalStorageState,
} from '../types/kaldflyte'

type JsonRecord = Record<string, unknown>

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

export class KaldFlyteApiClient {
  constructor(private readonly getToken: () => string | null) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.getToken()
    const headers = new Headers(init.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    const response = await fetch(path, {...init, headers})
    if (!response.ok) {
      const detail = await response.text()
      throw new ApiError(detail || response.statusText, response.status)
    }
    return response.json() as Promise<T>
  }

  me(): Promise<Principal> {
    return this.request('/api/auth/me')
  }

  createSimulation(payload: JsonRecord): Promise<JsonRecord> {
    return this.request('/api/v1/development/simulations', {
      method: 'POST', body: JSON.stringify(payload),
    })
  }

  simulationRuns(): Promise<{runs: Array<{run_id: string; status: string; variant: string}>}> {
    return this.request('/api/v1/development/simulations')
  }

  simulation(runId: string): Promise<JsonRecord> {
    return this.request(`/api/v1/development/simulations/${encodeURIComponent(runId)}`)
  }

  controlSimulation(runId: string, action: string): Promise<JsonRecord> {
    return this.request(
      `/api/v1/development/simulations/${encodeURIComponent(runId)}/${encodeURIComponent(action)}`,
      {method: 'POST'},
    )
  }

  compareSimulations(scenario: JsonRecord): Promise<JsonRecord> {
    return this.request('/api/v1/development/simulations/compare', {
      method: 'POST', body: JSON.stringify({scenario}),
    })
  }

  async fleetSites(): Promise<FleetSite[]> {
    const payload = await this.request<{sites: FleetSite[]}>('/api/v1/sites')
    return payload.sites
  }

  async portfolio(portfolioId: string): Promise<PortfolioState> {
    const payload = await this.request<JsonRecord>(
      `/api/v1/portfolios/${encodeURIComponent(portfolioId)}`,
    )
    return normalizePortfolio(payload)
  }

  siteApiStatus(siteId: string): Promise<SiteApiStatus> {
    return this.request(`/api/v1/sites/${encodeURIComponent(siteId)}`)
  }

  siteDashboard(siteId: string): Promise<SiteDashboard> {
    return this.request(`/api/ui/sites/${encodeURIComponent(siteId)}/dashboard`)
  }

  siteMarket(siteId: string): Promise<MarketView> {
    return this.request(`/api/v1/sites/${encodeURIComponent(siteId)}/market`)
  }

  siteForecast(siteId: string): Promise<ForecastView> {
    return this.request(`/api/v1/sites/${encodeURIComponent(siteId)}/forecasts`)
  }

  async siteEvents(siteId: string): Promise<SiteEventRecord[]> {
    const payload = await this.request<{events: SiteEventRecord[]}>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/events`,
    )
    return payload.events
  }

  assetHistory(siteId: string, assetId: string, rangeDays: number): Promise<AssetHistory> {
    return this.request(
      `/api/v1/sites/${encodeURIComponent(siteId)}/assets/${encodeURIComponent(assetId)}/history?range_days=${rangeDays}`,
    )
  }

  assetConfig(siteId: string, assetId: string): Promise<AssetConfig> {
    return this.request(
      `/api/v1/sites/${encodeURIComponent(siteId)}/assets/${encodeURIComponent(assetId)}/config`,
    )
  }

  async alarmHistory(
    siteId: string,
    options: {startUtc?: string; endUtc?: string; activeOnly?: boolean} = {},
  ): Promise<{alarms: AlarmRecord[]; site_id: string; start_utc: string; end_utc: string}> {
    const params = new URLSearchParams()
    if (options.startUtc) params.set('start_utc', options.startUtc)
    if (options.endUtc) params.set('end_utc', options.endUtc)
    if (options.activeOnly) params.set('active_only', 'true')
    const qs = params.toString()
    return this.request(
      `/api/v1/sites/${encodeURIComponent(siteId)}/alarms${qs ? `?${qs}` : ''}`,
    )
  }

  async acknowledgeAlarm(
    siteId: string,
    alarmId: string,
    operatorId: string,
  ): Promise<{alarm_id: string; acknowledged_by: string; acknowledged_at_utc: string}> {
    return this.request(
      `/api/v1/sites/${encodeURIComponent(siteId)}/alarms/${encodeURIComponent(alarmId)}/acknowledge`,
      {method: 'POST', body: JSON.stringify({operator_id: operatorId})},
    )
  }

  siteTimeline(siteId: string, hours = 24): Promise<SiteTimeline> {
    return this.request(
      `/api/v1/sites/${encodeURIComponent(siteId)}/timeline?hours=${hours}`,
    )
  }

  manualDispatch(siteId: string, level: number, durationMinutes: number) {
    return this.request<{
      ignored: boolean
      provider_name: string
      topic?: string
    }>(`/api/v1/sites/${encodeURIComponent(siteId)}/dispatch/manual`, {
      method: 'POST',
      body: JSON.stringify({level, duration_minutes: durationMinutes}),
    })
  }

  issueDispatch(siteId: string, payload: JsonRecord) {
    return this.request<JsonRecord>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/dispatch`,
      {method: 'POST', body: JSON.stringify(payload)},
    )
  }

  async siteOperations(siteId: string): Promise<SiteOperations> {
    const [apiStatus, dashboard, events, fleetSites, market, forecast] =
      await Promise.all([
        this.siteApiStatus(siteId),
        this.siteDashboard(siteId),
        this.siteEvents(siteId),
        this.fleetSites(),
        optional(() => this.siteMarket(siteId)),
        optional(() => this.siteForecast(siteId)),
      ])
    const activeEvent = market ? signalAsActiveEvent(market) : null
    return {
      status: normalizeSiteApiStatus(apiStatus, dashboard, activeEvent),
      dashboard,
      market,
      forecast,
      events,
      fleet_site: fleetSites.find((site) => site.site_id === siteId) || null,
    }
  }

  async portfolioDashboard(): Promise<PortfolioDashboard> {
    const fleetSites = await this.fleetSites()
    const portfolioIds = [...new Set(fleetSites.map((site) => site.portfolio_id))]
    const portfolios = await Promise.all(
      portfolioIds.map((portfolioId) => this.portfolio(portfolioId)),
    )
    const statusBySite = new Map(
      portfolios.flatMap((portfolio) =>
        portfolio.sites.map((site) => [site.site_id, site] as const),
      ),
    )
    const sites = await Promise.all(
      fleetSites.map(async (metadata) => {
        const [dashboard, market] = await Promise.all([
          optional(() => this.siteDashboard(metadata.site_id)),
          optional(() => this.siteMarket(metadata.site_id)),
        ])
        let status = statusBySite.get(metadata.site_id) || emptySiteStatus(metadata)
        if (market) {
          status = {...status, active_event: signalAsActiveEvent(market)}
        }
        return {status, metadata, dashboard, market}
      }),
    )
    return {portfolios, sites}
  }
}

async function optional<T>(load: () => Promise<T>): Promise<T | null> {
  try {
    return await load()
  } catch (error) {
    if (error instanceof ApiError) return null
    throw error
  }
}

export function normalizePortfolio(raw: JsonRecord): PortfolioState {
  const tenantId = stringValue(raw.tenant_id)
  const sites = arrayValue(raw.sites).map((site) =>
    normalizePortfolioSite(recordValue(site), tenantId),
  )
  return {
    portfolio_id: stringValue(raw.portfolio_id),
    tenant_id: tenantId,
    timestamp_utc: stringValue(raw.timestamp_utc),
    sites,
    total_available_kW: numberValue(raw.total_available_kW),
    minimum_portfolio_ttl_minutes: numberValue(
      raw.minimum_portfolio_ttl_minutes,
    ),
    minimum_event_duration_minutes: numberValue(
      raw.minimum_event_duration_minutes,
    ),
    sites_participating: numberValue(raw.sites_participating),
    sites_excluded: numberValue(raw.sites_excluded),
    grid_capacity: normalizeGridCapacity(
      recordValue(raw.grid_capacity),
      tenantId,
    ),
  }
}

function normalizeGridCapacity(
  raw: JsonRecord,
  tenantId: string,
): GridCapacityObject {
  return {
    entity_id: stringValue(raw.entity_id),
    entity_type: stringValue(raw.entity_type) || 'portfolio',
    tenant_id: stringValue(raw.tenant_id) || tenantId,
    timestamp_utc: stringValue(raw.timestamp_utc),
    available_capacity_kW: numberValue(raw.available_capacity_kW),
    duration_minutes: numberValue(raw.duration_minutes),
    confidence_pct: numberValue(raw.confidence_pct),
    recovery_profile: stringValue(raw.recovery_profile),
    rebound_risk_pct: numberValue(raw.rebound_risk_pct),
    thermal_mass_contribution_kW: numberValue(
      raw.thermal_mass_contribution_kW,
    ),
    thermal_storage_contribution_kW: numberValue(
      raw.thermal_storage_contribution_kW,
    ),
    net_demand_reduction_kW: numberValue(raw.net_demand_reduction_kW),
    capacity_prediction_complete:
      raw.capacity_prediction_complete === undefined
        ? true
        : booleanValue(raw.capacity_prediction_complete),
    excluded_site_ids_unknown_capacity: arrayValue(
      raw.excluded_site_ids_unknown_capacity,
    ).map(String),
    constraint_zone: nullableString(raw.constraint_zone),
    feeder_id: nullableString(raw.feeder_id),
    rebound_start_offset_minutes: numberValue(
      raw.rebound_start_offset_minutes,
    ),
    capacity_curve: arrayValue(raw.capacity_curve).map((point) => {
      const value = recordValue(point)
      return {
        duration_minutes: numberValue(value.duration_minutes),
        capacity_kW: numberValue(value.capacity_kW),
        mode: stringValue(value.mode) || undefined,
        limiting_factor: stringValue(value.limiting_factor) || undefined,
      }
    }),
  }
}

function normalizePortfolioSite(raw: JsonRecord, tenantId: string): SiteStatus {
  const storageSoc = nullableNumber(raw.thermal_storage_soc_pct)
  const freewheel = nullableNumber(raw.thermal_storage_freewheel_fraction) ?? 0
  const rebound = nullableNumber(raw.thermal_storage_rebound_fraction) ?? 0
  const assetIds = arrayValue(raw.asset_ids).map(String)
  return {
    site_id: stringValue(raw.site_id),
    tenant_id: stringValue(raw.tenant_id) || tenantId,
    timestamp_utc: stringValue(raw.timestamp_utc),
    available_capacity_kW: nullableNumber(raw.available_capacity_kW),
    net_demand_reduction_kW: nullableNumber(raw.net_demand_reduction_kW),
    capacity_prediction_valid:
      raw.capacity_prediction_valid === undefined
        ? raw.available_capacity_kW != null
        : booleanValue(raw.capacity_prediction_valid),
    capacity_prediction_reason_codes: arrayValue(
      raw.capacity_prediction_reason_codes,
    ).map(String),
    capacity_model_id: nullableString(raw.capacity_model_id),
    capacity_model_version: nullableString(raw.capacity_model_version),
    capacity_meter_boundary_id: nullableString(raw.capacity_meter_boundary_id),
    capacity_equipment_configuration_id: nullableString(
      raw.capacity_equipment_configuration_id,
    ),
    capacity_dispatch_duration_minutes: nullableNumber(
      raw.capacity_dispatch_duration_minutes,
    ),
    confidence_pct: numberValue(raw.confidence_pct),
    dispatch_readiness_pct: numberValue(
      raw.dispatch_readiness_pct,
      numberValue(raw.confidence_pct),
    ),
    ttl_minutes: numberValue(raw.ttl_minutes),
    assets: assetIds.map((assetId) =>
      emptyAsset(assetId, stringValue(raw.timestamp_utc)),
    ),
    thermal_storage_state:
      storageSoc === null
        ? null
        : {
            asset_id: assetIds[0] || 'thermal_storage',
            soc_pct: storageSoc,
            soc_kWh: null,
            freewheel_fraction: freewheel,
            rebound_fraction: rebound,
            ttl_contribution_minutes: null,
          },
    comms_confidence: numberValue(raw.comms_confidence),
    active_event: null,
    alarm_count_critical: 0,
    alarm_count_warning: booleanValue(raw.sensor_fault_active) ? 1 : 0,
  }
}

export function normalizeSiteApiStatus(
  raw: SiteApiStatus,
  dashboard: SiteDashboard,
  activeEvent: DispatchEvent | null,
): SiteStatus {
  const availability = recordValue(raw.availability_report?.payload)
  const latestState = recordValue(raw.latest_state?.payload)
  const timestamp =
    raw.latest_state?.timestamp_utc ||
    raw.availability_report?.timestamp_utc ||
    dashboard.last_telemetry_timestamp ||
    ''
  const assetResults = arrayValue(latestState.asset_results).map(recordValue)
  const assets = arrayValue(availability.assets).map((item) => {
    const asset = recordValue(item)
    const assetId = stringValue(asset.asset_id)
    const state = assetResults.find(
      (result) => stringValue(result.asset_id) === assetId,
    )
    return normalizeAsset(asset, state, timestamp)
  })
  const icecap = nullableRecord(availability.icecap)
  const storage = icecap ? normalizeStorage(icecap, dashboard.ttl_minutes) : null
  return {
    site_id: raw.site_id,
    tenant_id: raw.tenant_id,
    timestamp_utc: timestamp,
    available_capacity_kW: nullableNumber(availability.available_capacity_kW),
    net_demand_reduction_kW:
      nullableNumber(availability.net_demand_reduction_kW) ??
      nullableNumber(availability.available_capacity_kW),
    capacity_prediction_valid:
      availability.capacity_prediction_valid === undefined
        ? availability.available_capacity_kW != null
        : booleanValue(availability.capacity_prediction_valid),
    capacity_prediction_reason_codes: arrayValue(
      availability.capacity_prediction_reason_codes,
    ).map(String),
    capacity_model_id: nullableString(availability.capacity_model_id),
    capacity_model_version: nullableString(availability.capacity_model_version),
    capacity_meter_boundary_id: nullableString(
      availability.capacity_meter_boundary_id,
    ),
    capacity_equipment_configuration_id: nullableString(
      availability.capacity_equipment_configuration_id,
    ),
    capacity_dispatch_duration_minutes: nullableNumber(
      availability.capacity_dispatch_duration_minutes,
    ),
    confidence_pct: numberValue(
      availability.confidence_pct,
      dashboard.availability_score,
    ),
    dispatch_readiness_pct: numberValue(
      availability.dispatch_readiness_pct,
      dashboard.availability_score,
    ),
    ttl_minutes: numberValue(availability.ttl_minutes, dashboard.ttl_minutes ?? 0),
    assets,
    thermal_storage_state: storage,
    comms_confidence: numberValue(availability.comms_confidence),
    active_event: activeEvent,
    alarm_count_critical: dashboard.active_alarms.filter(
      (alarm) => alarm.severity === 'CRITICAL',
    ).length,
    alarm_count_warning: dashboard.active_alarms.filter(
      (alarm) => alarm.severity === 'WARNING',
    ).length,
  }
}

function normalizeAsset(
  raw: JsonRecord,
  state: JsonRecord | undefined,
  timestamp: string,
): AssetFlexibilityState {
  const excluded =
    booleanValue(raw.exclude_from_dispatch) ||
    booleanValue(state?.removed_from_dispatch_pool) ||
    booleanValue(state?.asset_alarmed)
  const reasons = arrayValue(raw.disqualification_reasons).map(String)
  if (excluded && reasons.length === 0) {
    reasons.push(
      booleanValue(state?.sensor_fault_active)
        ? 'sensor fault'
        : 'removed from dispatch pool',
    )
  }
  return {
    asset_id: stringValue(raw.asset_id),
    asset_type: stringValue(raw.asset_type) || 'thermal_asset',
    timestamp_utc: timestamp,
    temp_current: numberValue(raw.temp_current),
    temp_setpoint: numberValue(raw.temp_setpoint),
    temp_limit_max: numberValue(raw.temp_limit_max),
    temp_limit_min: numberValue(raw.temp_limit_min),
    drift_rate_per_minute: numberValue(raw.drift_rate_per_minute),
    ttl_minutes: numberValue(raw.ttl_minutes),
    flexibility_score: numberValue(raw.flexibility_score),
    thermal_reserve_margin: numberValue(raw.thermal_reserve_margin),
    power_kW: nullableNumber(raw.power_kW),
    sensor_fault:
      booleanValue(raw.sensor_fault) || booleanValue(state?.sensor_fault_active),
    in_precool: booleanValue(raw.in_precool),
    in_dispatch: booleanValue(raw.in_dispatch),
    in_rebound: booleanValue(raw.in_rebound),
    exclude_from_dispatch: excluded,
    disqualification_reasons: reasons,
  }
}

function normalizeStorage(
  raw: JsonRecord,
  ttlMinutes: number | null,
): ThermalStorageState {
  return {
    asset_id: stringValue(raw.asset_id) || 'thermal_storage',
    soc_pct: numberValue(raw.soc_pct),
    soc_kWh: nullableNumber(raw.soc_kWh),
    freewheel_fraction: numberValue(raw.freewheel_fraction, 1.0),
    rebound_fraction: numberValue(raw.rebound_fraction, 0.0),
    ttl_contribution_minutes: ttlMinutes,
  }
}

function signalAsActiveEvent(market: MarketView): DispatchEvent | null {
  const signal = market.signal_state || market.signal
  if (!signal || signal.level <= 0) return null
  const end = signal.event_end || signal.event_end_utc
  const start = signal.event_start || signal.event_start_utc
  if (!end || new Date(end).getTime() <= Date.now()) return null
  return {
    event_id: `${signal.source}-${start || 'active'}`,
    source: signal.source,
    level: signal.level,
    event_start_utc: start || '',
    event_end_utc: end,
  }
}

function emptySiteStatus(metadata: FleetSite): SiteStatus {
  return {
    site_id: metadata.site_id,
    tenant_id: metadata.tenant_id,
    timestamp_utc: '',
    available_capacity_kW: null,
    net_demand_reduction_kW: null,
    capacity_prediction_valid: false,
    capacity_prediction_reason_codes: ['availability_unavailable'],
    capacity_model_id: null,
    capacity_model_version: null,
    capacity_meter_boundary_id: null,
    capacity_equipment_configuration_id: null,
    capacity_dispatch_duration_minutes: null,
    confidence_pct: 0,
    dispatch_readiness_pct: 0,
    ttl_minutes: 0,
    assets: [],
    thermal_storage_state: null,
    comms_confidence: 0,
    active_event: null,
    alarm_count_critical: 0,
    alarm_count_warning: 0,
  }
}

function emptyAsset(assetId: string, timestamp: string): AssetFlexibilityState {
  return {
    asset_id: assetId,
    asset_type: 'thermal_asset',
    timestamp_utc: timestamp,
    temp_current: 0,
    temp_setpoint: 0,
    temp_limit_max: 0,
    temp_limit_min: 0,
    drift_rate_per_minute: 0,
    ttl_minutes: 0,
    flexibility_score: 0,
    thermal_reserve_margin: 0,
    power_kW: 0,
    sensor_fault: false,
    in_precool: false,
    in_dispatch: false,
    in_rebound: false,
    exclude_from_dispatch: false,
    disqualification_reasons: [],
  }
}

function recordValue(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonRecord)
    : {}
}

function nullableRecord(value: unknown): JsonRecord | null {
  const record = recordValue(value)
  return Object.keys(record).length ? record : null
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null
}

function numberValue(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function booleanValue(value: unknown): boolean {
  return value === true
}

export function alarmsBySeverity(alarms: Alarm[]): Alarm[] {
  const order: Record<string, number> = {CRITICAL: 0, WARNING: 1, INFO: 2}
  return [...alarms].sort(
    (a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3),
  )
}
